import React, { useState, useRef, useCallback, useMemo } from "react";
import { MultiStep, StepProps } from "@org/ui";
import { ClassForm, ClassFormRef } from "../forms/class";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@org/ui";
import { Button } from "@org/ui";
import { Alert, AlertTitle, AlertDescription } from "@org/ui";
import { InfoIcon, PlusIcon } from "lucide-react";
import { trpc } from "@org/clients/school";
import { toast } from "sonner";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useDndSensors } from "../shared/use-dnd-sensors";
import {
  ContentType,
  ContentItem,
  Block,
  createNewBlock,
  createNewContent,
} from "@org/ui";
import { ExerciseType } from "@org/model";
import { ClassSummary } from "./class-summary";
import { BlockContentEditor } from "./block-content-editor";

import { useContentRenderer } from "../hooks/use-content-renderer";
import { resolveAfterSave, useStepNavigation } from "../shared/step-navigation";
import { UnsavedChangesDialog } from "../shared/unsaved-changes-dialog";
import { useBlocksData } from "./hooks/use-blocks-data";
import { useBlocksReducer } from "./hooks/use-blocks-reducer";
import { ThreeDots, useAuthorization } from "@org/ui";
import { useTranslation } from "react-i18next";

type ClassMultiStepFormProps = {
  defaultValues: any;
  onSubmit: () => void;
  onClose: () => void;
  isEditing: boolean;
};


export const ClassMultiStepForm: React.FC<ClassMultiStepFormProps> = ({
  defaultValues,
  onSubmit,
  onClose,
  isEditing
}) => {
  const { t } = useTranslation('admin');
  const steps: StepProps[] = [
    { title: t('classes.form.steps.classInfo') },
    { title: t('classes.form.steps.classContent') },
    { title: t('classes.form.steps.summary') }
  ];
  const states = ["classInfo", "classContent", "summary"];
  // Creating = wizard (steps unlock as they are saved); editing = free navigation.
  const nav = useStepNavigation(states.length, isEditing);
  const { currentIndex } = nav;
  const [classData, setClassData] = useState<Record<string, any>>(defaultValues || {});
  const [blocks, dispatch] = useBlocksReducer(defaultValues?.blocks || []);
  const [isSaving, setIsSaving] = useState(false);
  const saveSignalRef = useRef<number>(0);
  // Unsaved edits on the class-info step (the form unmounts when the step
  // changes, so leaving it would silently drop them). Block edits live in
  // this component's state and survive step changes; they are written by
  // "Save & finish" on the summary step.
  const [infoDirty, setInfoDirty] = useState(false);
  const [infoFormKey, setInfoFormKey] = useState(0);
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const utils = trpc.useUtils();
  const createBlockMutation = trpc.blocks.createBlock.useMutation();
  const deleteBlockMutation = trpc.blocks.deleteBlock.useMutation();

  const renderContent = useContentRenderer();
  const { hasPermission } = useAuthorization();
  const canDelete = hasPermission('blocks:delete');

  const sensors = useDndSensors();
  const formRef = useRef<ClassFormRef>(null);

  // Custom hook to handle block data fetching and processing
  const { isLoading } = useBlocksData({
    classId: classData.classId,
    isEditing,
    onBlocksLoaded: (loadedBlocks) => {dispatch({ type: "SET_BLOCKS", payload: loadedBlocks })},
    saveSignal: saveSignalRef.current
  });

  const handleNext = useCallback(() => {
    if (currentIndex === 0 && formRef.current) {
      setPendingTarget(null);
      formRef.current.submit();
    } else {
      nav.next();
    }
  }, [currentIndex, nav]);

  /** Where to land after the class-info step was saved: the step asked for, else the next one. */
  const goAfterSave = useCallback(
    (target: number) => {
      if (target === currentIndex + 1) {
        nav.next(); // unlocks the step in wizard mode
      } else {
        nav.goTo(target);
      }
    },
    [currentIndex, nav],
  );

  const handleClassFormSubmit = useCallback((data: any) => {
    // Update classData with the submitted form data
    setClassData(prevData => ({ ...prevData, ...data }));
    // The saved values become the form's new defaults (clean form on return).
    setInfoDirty(false);
    setInfoFormKey((k) => k + 1);
    setUnsavedOpen(false);
    const target = resolveAfterSave(pendingTarget, currentIndex, states.length);
    setPendingTarget(null);
    goAfterSave(target);
  }, [currentIndex, pendingTarget, goAfterSave, states.length]);

  /** Header click: free when editing, only unlocked steps when creating; guard unsaved class info. */
  const requestStep = useCallback((target: number) => {
    if (target === currentIndex || !nav.canReach(target)) return;
    if (currentIndex === 0 && infoDirty) {
      setPendingTarget(target);
      setUnsavedOpen(true);
      return;
    }
    nav.goTo(target);
  }, [currentIndex, infoDirty, nav]);

  const handleUnsavedSave = useCallback(() => {
    // The form's onSubmit (handleClassFormSubmit) moves to `pendingTarget`
    // once the class is saved; on a validation error the step stays open.
    setUnsavedOpen(false);
    formRef.current?.submit();
  }, []);

  const handleUnsavedDiscard = useCallback(() => {
    setUnsavedOpen(false);
    setInfoDirty(false);
    setInfoFormKey((k) => k + 1); // remount = back to the last saved values
    if (pendingTarget !== null) nav.goTo(pendingTarget);
    setPendingTarget(null);
  }, [nav, pendingTarget]);

  const handleUnsavedCancel = useCallback(() => {
    setUnsavedOpen(false);
    setPendingTarget(null);
  }, []);

  const stepLabels = useMemo(
    () => ({
      completed: t('common.steps.completed'),
      current: t('common.steps.current'),
      pending: t('common.steps.pending'),
    }),
    [t],
  );

  const handleAddBlock = useCallback(() => {
    const newBlock = createNewBlock(blocks.length);
    dispatch({ type: "ADD_BLOCK", payload: newBlock });
  }, [blocks.length]);

  const handleUpdateBlock = useCallback((updatedBlock: Block) => {
    dispatch({ type: "UPDATE_BLOCK", payload: updatedBlock });
  }, []);

  const handleDeleteBlock = useCallback((blockId: string) => {
    dispatch({ type: "DELETE_BLOCK", payload: blockId });
  }, []);

  const handleUndoDeleteBlock = useCallback((blockId: string) => {
    dispatch({ type: "UNDO_DELETE_BLOCK", payload: blockId });
  }, []);

  const handleAddContent = useCallback((blockId: string, type: ContentType, exerciseSubtype?: ExerciseType) => {
    const block = blocks.find(b => b.id === blockId);
    const newContent = createNewContent(type, block ? block.contents.length : 0, exerciseSubtype);
    dispatch({ type: "ADD_CONTENT", blockId, content: newContent });
  }, [blocks]);

  const handleUpdateContent = useCallback((blockId: string, updatedContent: ContentItem) => {
    dispatch({ type: "UPDATE_CONTENT", blockId, content: updatedContent });
  }, []);

  const handleDeleteContent = useCallback((blockId: string, contentId: string) => {
    dispatch({ type: "DELETE_CONTENT", blockId, contentId });
  }, []);

  const handleUndoDeleteContent = useCallback((blockId: string, contentId: string) => {
    dispatch({ type: "UNDO_DELETE_CONTENT", blockId, contentId });
  }, []);

  const handleMoveContent = useCallback(
    (fromBlockId: string, toBlockId: string, contentId: string, insertIndex?: number) => {
      dispatch({ type: "MOVE_CONTENT", fromBlockId, toBlockId, contentId, insertIndex });
    },
    [],
  );

  const handleSplitBlock = useCallback(
    (blockId: string, splitAfterContentId: string) => {
      const sourceBlock = blocks.find(block => block.id === blockId);
      if (!sourceBlock) return;

      const splitIndex = sourceBlock.contents.findIndex(content => content.id === splitAfterContentId);
      if (splitIndex < 0 || splitIndex >= sourceBlock.contents.length - 1) {
        return;
      }

      const baseName = sourceBlock.name?.trim() || "Block";
      const nextBlock = {
        ...createNewBlock(blocks.length),
        name: `${baseName} (Split)`,
        description: sourceBlock.description || "",
      };

      dispatch({
        type: "SPLIT_BLOCK",
        blockId,
        splitAfterContentId,
        newBlock: nextBlock,
      });
    },
    [blocks],
  );

  const handleDragEnd = useCallback((event: DragEndEvent, blockId?: string) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      if (blockId) {
        const block = blocks.find(b => b.id === blockId);
        if (!block) return;
        const oldIndex = block.contents.findIndex(c => c.id === active.id);
        const newIndex = block.contents.findIndex(c => c.id === over.id);
        const newContents = arrayMove(block.contents, oldIndex, newIndex).map((content, index) => ({
          ...content,
          order: index
        }));
        dispatch({ type: "REORDER_CONTENT", blockId, contents: newContents });
      } else {
        const oldIndex = blocks.findIndex(b => b.id === active.id);
        const newIndex = blocks.findIndex(b => b.id === over.id);
        const newBlocks = arrayMove(blocks, oldIndex, newIndex).map((block, index) => ({
          ...block,
          order: index
        }));
        dispatch({ type: "REORDER_BLOCKS", payload: newBlocks });
      }
    }
  }, [blocks]);

  const handleFinalSubmit = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (blocks.length > 0 && classData.classId) {
        await Promise.all(
          blocks.map(async (blk) => {
            if (blk.deleted) {
              return deleteBlockMutation.mutateAsync({ blockId: blk.id as string });
            } else {
              const blockInput = {
                blockId: blk.id,
                classId: classData.classId,
                name: blk.name,
                description: blk.description,
                order: blk.order,
                contents: blk.contents
                  .filter(c => !c.deleted)
                  .map((c) => ({
                    contentId: c.id,
                    contentType: c.type.toLowerCase(),
                    title: c.data.title ?? "",
                    description: c.data.description ?? "",
                    order: c.order,
                    data: c.data//transformContentDataForServer(c)
                  }))
              };
              // @ts-ignore
              return createBlockMutation.mutateAsync(blockInput);
            }
          })
        );

        // Invalidate related caches so future opens see fresh data
        await Promise.all([
          utils.blocks.getBlocksByClass.invalidate({ classId: classData.classId }),
          utils.blocks.getBlockContents.invalidate(),
          utils.content.invalidate()
        ]);

        // Increment the save signal to trigger an immediate data refresh while mounted
        saveSignalRef.current += 1;
      }
      toast.success(t('classes.form.toasts.saveSuccess'));
      onSubmit();
    } catch (error) {
      console.error("Error saving blocks:", error);
      toast.error(t('classes.form.toasts.saveError'));
    } finally {
      setIsSaving(false);
    }
  }, [blocks, classData, createBlockMutation, onSubmit, deleteBlockMutation, utils, isSaving, t]);

  const renderClassInfo = () => (
    <Card>
      <CardHeader>
        <CardTitle>{t('classes.form.classInfo.title')}</CardTitle>
        <CardDescription>{t('classes.form.classInfo.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ClassForm
          key={infoFormKey}
          defaultValues={classData}
          onSubmit={handleClassFormSubmit}
          onClose={onClose}
          hideButtons={true}
          ref={formRef}
          onDirtyChange={setInfoDirty}
        />
      </CardContent>
    </Card>
  );

  const renderClassContent = () => (
    <Card>
      <CardHeader className="pb-3 flex justify-between align-left">
        <div>
          <CardTitle>{t('classes.form.classContent.title')}</CardTitle>
          <CardDescription>{t('classes.form.classContent.description')}</CardDescription>
        </div>
        <div>
          <Button variant="outline" onClick={handleAddBlock} aria-label={t('classes.form.classContent.addBlock')}>
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{t('classes.form.classContent.addBlock')}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Alert className="my-4">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>{t('classes.form.loading.title')}</AlertTitle>
            <AlertDescription>
              {t('classes.form.loading.description')}
            </AlertDescription>
          </Alert>
        ) : blocks.length === 0 ? (
          <Alert className="my-4">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>{t('classes.form.empty.title')}</AlertTitle>
            <AlertDescription>
              {t('classes.form.empty.description')}
            </AlertDescription>
          </Alert>
        ) : (
          <BlockContentEditor
            blocks={blocks}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onUpdateBlock={handleUpdateBlock}
            onDeleteBlock={handleDeleteBlock}
            onUndoDeleteBlock={handleUndoDeleteBlock}
            onAddContent={handleAddContent}
            onUpdateContent={handleUpdateContent}
            onDeleteContent={handleDeleteContent}
            onUndoDeleteContent={handleUndoDeleteContent}
            renderContent={renderContent}
            onMoveContent={handleMoveContent}
            onSplitBlock={handleSplitBlock}
            canDelete={canDelete}
          />
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="flex flex-col w-full min-h-screen bg-muted/50">
      <div className="w-full px-8 py-4 bg-card border-b border-border shadow-sm">
        <MultiStep
          steps={steps}
          currentStep={currentIndex}
          onChange={requestStep}
          mode={nav.mode}
          reachable={nav.reachable}
          labels={stepLabels}
        />
      </div>
      <div className="w-full flex-grow p-6 max-w-5xl mx-auto mt-6">
        {states[currentIndex] === "classInfo" && renderClassInfo()}
        {states[currentIndex] === "classContent" && renderClassContent()}
        {states[currentIndex] === "summary" && (
          <ClassSummary classData={classData} blocks={blocks} />
        )}
      </div>
      <div className="w-full py-4 px-6 flex justify-between bg-card border-t border-border shadow-sm mt-auto">
        {currentIndex > 0 && (
          <Button variant="outline" onClick={nav.back}>
            {t('classes.form.actions.back')}
          </Button>
        )}
        <div className="ml-auto">
          {currentIndex < states.length - 1 ? (
            <Button variant="outline" onClick={handleNext}>{t('classes.form.actions.next')}</Button>
          ) : (
            <Button
              onClick={handleFinalSubmit}
              variant="outline"
              disabled={isSaving}
              className="disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSaving ? (
                <span className="inline-flex items-center">
                  <ThreeDots />
                </span>
              ) : (
                t('classes.form.actions.saveFinish')
              )}
            </Button>
          )}
        </div>
      </div>
      <UnsavedChangesDialog
        open={unsavedOpen}
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={handleUnsavedCancel}
      />
    </div>
  );
};

export default ClassMultiStepForm;
